import React, { useState, useEffect } from 'react';
import { ShieldAlert, X, Send, UserRound, Phone, AlertCircle } from 'lucide-react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import './RejectVerificationModal.css';

const QUICK_SUGGESTIONS = [
    'Please upload a clear, front-facing profile photo.',
    'Please complete your education and employment details properly.',
    'Please enter accurate family details and hometown/district.',
    'Please provide valid contact and personal information.',
];

const RejectVerificationModal = ({ open, target, isProfile = false, onClose, onSuccess }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setReason(target?.verificationRejectReason || '');
        } else {
            setReason('');
        }
    }, [open, target]);

    if (!open || !target) return null;

    const name = target.name || target.user?.name || 'Member';
    const memberId = target.memberId || target.user?.memberId;
    const mobile = target.mobile || target.user?.mobile;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = reason.trim();
        if (!trimmed) {
            toast.error('Please enter the reason for rejection');
            return;
        }

        setSubmitting(true);
        try {
            const endpoint = isProfile
                ? `/admin/profiles/${target._id}/reject`
                : `/admin/users/${target._id}/reject-verification`;

            const res = await API.put(endpoint, { reason: trimmed });
            toast.success(`Rejection message sent to ${name}`);
            onSuccess?.(res.data);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reject verification');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="reject-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
            <div className="reject-modal" onClick={e => e.stopPropagation()}>
                <header className="reject-modal__header">
                    <div className="reject-modal__title-row">
                        <span className="reject-modal__icon">
                            <ShieldAlert size={20} />
                        </span>
                        <div>
                            <h3>Reject Profile Verification</h3>
                            <p>Send feedback to {name} explaining what needs to be updated.</p>
                        </div>
                    </div>
                    <button type="button" className="reject-modal__close" onClick={onClose} aria-label="Close">
                        <X size={18} />
                    </button>
                </header>

                <div className="reject-modal__member-pill">
                    <div className="reject-modal__member-meta">
                        <strong><UserRound size={13} /> {name}</strong>
                        {memberId && <span>ID: {memberId}</span>}
                        {mobile && <span><Phone size={11} /> {mobile}</span>}
                    </div>
                    <span className="reject-modal__badge">Notification recipient</span>
                </div>

                <div className="reject-modal__info-notice">
                    <AlertCircle size={15} />
                    <span>This rejection message will appear <strong>only in this member's notification bell</strong> and dashboard banner.</span>
                </div>

                <form onSubmit={handleSubmit} className="reject-modal__form">
                    <label className="reject-modal__label">
                        Reason for rejection / Instructions:
                        <textarea
                            rows={4}
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g., Please upload a clear photo and update your education and job details properly..."
                            required
                            maxLength={1000}
                            autoFocus
                        />
                    </label>

                    <div className="reject-modal__suggestions">
                        <span className="reject-modal__suggestions-label">Quick suggestions (click to insert):</span>
                        <div className="reject-modal__chips">
                            {QUICK_SUGGESTIONS.map((text, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className="reject-modal__chip"
                                    onClick={() => setReason(prev => (prev ? `${prev} ${text}` : text))}
                                >
                                    + {text}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="reject-modal__actions">
                        <button type="button" className="reject-modal__btn-cancel" onClick={onClose} disabled={submitting}>
                            Cancel
                        </button>
                        <button type="submit" className="reject-modal__btn-submit" disabled={submitting || !reason.trim()}>
                            <Send size={15} />
                            {submitting ? 'Sending…' : 'Send rejection message'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RejectVerificationModal;
